import { useState, useEffect, useCallback } from "react";
import { FolderOpen, FileText, FileSpreadsheet, Trash2, ExternalLink, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { getAnalyses } from "@/lib/api";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyFiles({ refreshKey, onSwitchAnalysis }) {
  const { user } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user || user._id === "demo") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [uRes, aRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/uploads`, { withCredentials: true, headers }),
        getAnalyses(),
      ]);
      setUploads(uRes.data.uploads || []);
      setAnalyses(aRes.data.analyses || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${BACKEND_URL}/api/uploads/${id}`, { withCredentials: true, headers });
      setUploads((prev) => prev.filter((u) => u.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const isGuest = !user || user.email === "demo@demo.com";

  if (isGuest) {
    return (
      <div className="bg-white border border-[#E7E5DF] rounded-md p-12 text-center animate-fade-in" data-testid="my-files-guest">
        <FolderOpen className="w-12 h-12 text-[#8A9A86] mx-auto mb-4" />
        <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#1C1917] mb-2">
          Your Files & History
        </h3>
        <p className="text-[#57534E] text-sm max-w-md mx-auto">
          Create an account to upload your own bank statements and save your analyses. Your upload history and past analyses will be stored here.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1A362D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-files-section">
      <div>
        <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold text-[#1C1917]">
          My Files & History
        </h2>
        <p className="text-sm text-[#57534E] mt-1">
          Your uploaded files and past analyses
        </p>
      </div>

      {/* Past Analyses */}
      <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#E7E5DF]">
          <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
            My Analyses ({analyses.length})
          </h3>
        </div>
        {analyses.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#78716C]">No analyses yet</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
            {analyses.map((a) => (
              <button
                key={a.id}
                data-testid={`analysis-history-${a.id}`}
                onClick={() => onSwitchAnalysis?.(a.id, a)}
                className="text-left border border-[#E7E5DF] rounded-md p-4 hover:border-[#1A362D] hover:bg-[#1A362D]/5 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-[#1C1917]">{a.name}</p>
                    {a.business_name && <p className="text-xs text-[#78716C]">{a.business_name}</p>}
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#78716C] flex-shrink-0" />
                </div>
                <div className="flex gap-3 mt-2 text-xs text-[#78716C]">
                  <span>{a.transaction_count || 0} transactions</span>
                  <span>{a.offer_count || 0} offers</span>
                </div>
                <p className="text-xs text-[#78716C] mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Upload History */}
      <div className="bg-white border border-[#E7E5DF] rounded-md overflow-hidden">
        <div className="p-4 border-b border-[#E7E5DF]">
          <h3 className="text-lg font-['Playfair_Display'] font-semibold text-[#1C1917]">
            Upload History ({uploads.length})
          </h3>
        </div>
        {uploads.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#78716C]">
            No files uploaded yet. Upload bank statements to see them here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1EFEA]">
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">File</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Type</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Analysis</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] text-right">Transactions</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C]">Date</TableHead>
                  <TableHead className="text-xs tracking-[0.15em] uppercase font-bold text-[#78716C] w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {uploads.map((u) => (
                  <TableRow key={u.id} className="table-row-hover">
                    <TableCell className="py-3 flex items-center gap-2">
                      {u.file_type === "pdf" ? <FileText className="w-4 h-4 text-[#E07A5F]" /> : <FileSpreadsheet className="w-4 h-4 text-[#2D4A3E]" />}
                      <span className="text-sm text-[#1C1917] truncate max-w-[200px]">{u.filename}</span>
                    </TableCell>
                    <TableCell className="py-3">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${u.file_type === "pdf" ? "bg-[#E07A5F]/10 text-[#E07A5F]" : "bg-[#2D4A3E]/10 text-[#2D4A3E]"}`}>
                        {u.file_type}
                      </span>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-[#57534E]">
                      <button
                        onClick={() => onSwitchAnalysis?.(u.analysis_id)}
                        className="hover:text-[#1A362D] hover:underline"
                      >
                        {u.analysis_name || u.analysis_id?.slice(0, 8)}
                      </button>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-right font-['IBM_Plex_Mono']">{u.transaction_count}</TableCell>
                    <TableCell className="py-3 text-sm text-[#78716C] font-['IBM_Plex_Mono']">
                      {u.uploaded_at ? new Date(u.uploaded_at).toLocaleDateString() : ""}
                    </TableCell>
                    <TableCell className="py-3">
                      <button onClick={() => handleDelete(u.id)} className="text-[#78716C] hover:text-[#E07A5F]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
